// SPDX-License-Identifier: Apache-2.0

// IntegrationsSetupWizard (Iris).
//
// Top-level modal that shells the per-flow panels. Two cards (Slack, Gmail).
// Reads /api/integrations/status on mount + after each successful flow.
//
// Endpoints called from the panels (not here):
//   POST /api/integrations/slack/test, /slack/configure
//   POST /api/integrations/gmail/upload-client-secret, /gmail/start-auth
//   GET  /api/integrations/gmail/auth-status (poll)
//
// Same-origin only — every endpoint refuses requests without
// Origin: http://localhost:5173. Default fetch sends it automatically.
//
// Token-storage rule: tokens NEVER touch localStorage. Only the wizard's
// step-progress is persisted. Inputs are scrubbed after a successful save.

import {
  Show,
  createSignal,
  createEffect,
  onMount,
  onCleanup,
  type JSX,
} from "solid-js";
import SlackSetupPanel from "./SlackSetupPanel";
import GmailSetupPanel from "./GmailSetupPanel";
import "./IntegrationsSetupWizard.css";

const PROGRESS_KEY = "tether:setup-progress";

export type Flow = "slack" | "gmail";

export interface IntegrationsSetupWizardProps {
  open: boolean;
  onClose: () => void;
  initialFlow?: Flow;
  onFlowComplete?: (flow: Flow) => void;
}

interface PersistedProgress {
  slackStep: number;
  gmailStep: number;
}

function clampInt(n: unknown, min: number, max: number): number {
  const v = typeof n === "number" && Number.isFinite(n) ? Math.floor(n) : min;
  return Math.max(min, Math.min(max, v));
}

function loadProgress(): PersistedProgress {
  if (typeof localStorage === "undefined") return { slackStep: 1, gmailStep: 1 };
  try {
    const raw = localStorage.getItem(PROGRESS_KEY);
    if (!raw) return { slackStep: 1, gmailStep: 1 };
    const parsed = JSON.parse(raw) as Partial<PersistedProgress>;
    return {
      slackStep: clampInt(parsed.slackStep, 1, 3),
      gmailStep: clampInt(parsed.gmailStep, 1, 4),
    };
  } catch {
    return { slackStep: 1, gmailStep: 1 };
  }
}

function saveProgress(p: PersistedProgress): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(p));
  } catch {
    /* quota / private mode — ignore */
  }
}

export default function IntegrationsSetupWizard(
  props: IntegrationsSetupWizardProps
): JSX.Element {
  const [flow, setFlow] = createSignal<Flow>(props.initialFlow ?? "slack");
  const initial = loadProgress();
  const [slackStep, setSlackStep] = createSignal<number>(initial.slackStep);
  const [gmailStep, setGmailStep] = createSignal<number>(initial.gmailStep);

  // Sync flow + step state on every open. Otherwise the parent's `initialFlow`
  // change wouldn't take effect on the second open of the same modal.
  createEffect(() => {
    if (props.open) {
      setFlow(props.initialFlow ?? "slack");
      const fresh = loadProgress();
      setSlackStep(fresh.slackStep);
      setGmailStep(fresh.gmailStep);
    }
  });

  createEffect(() => {
    saveProgress({ slackStep: slackStep(), gmailStep: gmailStep() });
  });

  onMount(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && props.open) props.onClose();
    };
    window.addEventListener("keydown", onKey);
    onCleanup(() => window.removeEventListener("keydown", onKey));
  });

  return (
    <Show when={props.open}>
      <div
        class="iris-wiz__overlay"
        data-testid="comms-wizard-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget) props.onClose();
        }}
      >
        <div
          class="iris-wiz__modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="iris-wiz-title"
          data-testid="comms-wizard"
        >
          <header class="iris-wiz__head">
            <div>
              <span class="iris-wiz__eyebrow">integrations · setup</span>
              <h2 id="iris-wiz-title" class="iris-wiz__title">
                Plug in Slack and Gmail.
              </h2>
              <p class="iris-wiz__lede">
                Tokens stay on your machine — written to{" "}
                <code>~/.tether-brain/integrations.json</code> with{" "}
                <code>chmod 0600</code>. Never to the cloud, never to
                localStorage.
              </p>
            </div>
            <button
              type="button"
              class="iris-wiz__close"
              aria-label="close wizard"
              data-testid="comms-wizard-close"
              onClick={() => props.onClose()}
            >
              ×
            </button>
          </header>

          <nav class="iris-wiz__tabs" role="tablist">
            <button
              type="button"
              role="tab"
              class="iris-wiz__tab"
              aria-selected={flow() === "slack"}
              classList={{ active: flow() === "slack" }}
              data-testid="comms-wizard-tab-slack"
              onClick={() => setFlow("slack")}
            >
              <span class="iris-wiz__tab-glyph iris-wiz__tab-glyph--slack">
                #
              </span>
              slack
              <span class="iris-wiz__tab-step">step {slackStep()}/3</span>
            </button>
            <button
              type="button"
              role="tab"
              class="iris-wiz__tab"
              aria-selected={flow() === "gmail"}
              classList={{ active: flow() === "gmail" }}
              data-testid="comms-wizard-tab-gmail"
              onClick={() => setFlow("gmail")}
            >
              <span class="iris-wiz__tab-glyph iris-wiz__tab-glyph--gmail">
                @
              </span>
              gmail
              <span class="iris-wiz__tab-step">step {gmailStep()}/4</span>
            </button>
          </nav>

          <Show when={flow() === "slack"}>
            <SlackSetupPanel
              step={slackStep()}
              setStep={setSlackStep}
              onDone={() => props.onFlowComplete?.("slack")}
            />
          </Show>
          <Show when={flow() === "gmail"}>
            <GmailSetupPanel
              step={gmailStep()}
              setStep={setGmailStep}
              onDone={() => props.onFlowComplete?.("gmail")}
            />
          </Show>
        </div>
      </div>
    </Show>
  );
}
